import { useEffect } from "react";
import { sendViews } from "../api/usage.js";
import { createScreenClock, type Visit } from "./screen-clock.js";

/**
 * The visit in this browser, shared by its tabs. Not a token and nothing
 * secret: a random id and a time, which is all a visit is.
 */
const VISIT_KEY = "crux.usage.visit";

function loadVisit(): Visit | null {
  const raw = window.localStorage.getItem(VISIT_KEY);
  if (raw === null) return null;
  const [id, at] = raw.split(" ");
  const lastActive = Number(at);
  // Unreadable is a visit nobody can continue, so a new one starts.
  return id === undefined || id === "" || !Number.isFinite(lastActive)
    ? null
    : { id, lastActive };
}

function saveVisit(visit: Visit): void {
  window.localStorage.setItem(VISIT_KEY, `${visit.id} ${String(visit.lastActive)}`);
}

/**
 * One clock for the tab: there is one screen on show at a time, whichever
 * component asks.
 *
 * A failed send is not caught. Nobody using Crux can act on it, so it is not
 * shown to them; it surfaces in the browser console as an unhandled rejection
 * (the clock hands every send to `void`), where whoever is looking can see it,
 * rather than vanishing into a `catch`.
 */
const clock = createScreenClock({
  now: () => Date.now(),
  newVisitId: () => crypto.randomUUID(),
  send: sendViews,
  release: import.meta.env.VITE_CRUX_RELEASE,
  loadVisit,
  saveVisit,
});

const isVisible = (): boolean => document.visibilityState === "visible";

/**
 * Records which screen is on show in which workspace, and for how long it was
 * visible, for the operator panel's Usage page. Not data fetching: it only
 * sends, as a screen is left.
 */
export function useScreenUsage(workspaceId: string | null, screen: string): void {
  useEffect(() => {
    const onVisibility = (): void => {
      if (isVisible()) clock.reveal();
      else clock.hide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", clock.hide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", clock.hide);
      // The shell goes away on sign-out, which ended the view itself first
      // (`endScreenUsage`), or when the session is lost, whose view the server
      // would refuse anyway.
      clock.forget();
    };
  }, []);

  useEffect(() => {
    if (workspaceId === null) void clock.stop();
    else clock.show(workspaceId, screen, isVisible());
  }, [workspaceId, screen]);
}

/**
 * Signing out: the screen on show ends now, while the session still stands,
 * so the server accepts its view. Settles once it has been sent.
 */
export function endScreenUsage(): Promise<void> {
  return clock.stop();
}
